import { truncateToDecimals } from 'utils/format-number';
import {
  AssetField,
  Button,
  Heading,
  InfoText,
  RadioButton,
  TextFieldRef,
  Typography,
} from '@altify/components';
import { useDismissableModal } from '@hooks/useOneTimeModal';
import { useEffect, useRef, useState } from 'react';
import InfoModal from '../components/InfoModal/InfoModal';
import ReviewOrderModal from '../components/ReviewOrderModal/ReviewOrderModal';
import { tradeSharedContent } from '../tradeShared.content';

import {
  formatAssetAmount,
  getAssetPrecision,
  useContent,
} from '@altify/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import {
  useFunctionalCurrencyWithHoldings,
  useProductWithHoldings,
} from 'hooks/products/useProducts';
import { View } from 'react-native';
import { useGetProfileQuery } from 'store/slices/profile-api';
import {
  useAcceptQuoteMutation,
  useGetQuoteDataQuery,
  useInitiateQuoteMutation,
} from 'store/slices/trade-api';
import { z } from 'zod';
import TermsModal from '../components/TermsModal/TermsModal';
import CryptoDisclosureModal from '../components/CryptoDisclosureModal/CryptoDisclosureModal';
import SelectionCurrencyModal from '../components/SelectionCurrencyModal/SelectionCurrencyModal';
import SmartTradeModal from '../components/SmartTradeModal/SmartTradeModal';
import SubmitOrderModal from '../components/SubmitOrderModal/SubmitOrderModal';
import { TradeSide } from '../types/TradeSide';
import { calculateTradeData } from '../utils/trade-helpers';
import { content } from './route.content';
import {
  EVENTS,
  trackEvent,
  tradeEventFields,
  TRANSACTION_TYPE,
} from 'utils/analytics';
import StockDisclosureModal from '../components/StockDisclosureModal/StockDisclosureModal';
import { ActiveProductDtoTypeEnum } from 'services/generated';
import { formatCurrency } from 'utils/currencies';

export const Route = createFileRoute('/_authenticated/trade/buy-now')({
  component: BuyNowScreen,
  validateSearch: zodValidator(
    z.object({
      productCode: z.string().optional(),
    }),
  ),
});

type InfoType = 'tradePrice' | 'tradingFee' | 'smartTrade' | 'rewardLevel';
const percentageButtonValues = [25, 50, 75, 100];
const type = 'buy';

function BuyNowScreen() {
  const { productCode: toProductCode } = Route.useSearch();
  const router = useRouter();
  const { data: profile } = useGetProfileQuery();
  const { functionalCurrency } = useFunctionalCurrencyWithHoldings();
  const localizedContent = useContent(content, tradeSharedContent);
  const fromAssetFieldRef = useRef<TextFieldRef>(null);

  const [infoType, setInfoType] = useState<InfoType>('tradePrice');
  const [selectingSide, setSelectingSide] = useState<TradeSide>('from');
  const [fromAmount, setFromAmount] = useState<number | null>(null);
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(
    null,
  );

  const [fromProductCode, setFromProductCode] = useState<string | null>(
    profile?.minimumTradingAmount &&
      profile?.minimumTradingAmount <= functionalCurrency?.availableAmount!
      ? functionalCurrency?.code!
      : null,
  );

  const [reviewOrderIsOpen, setReviewOrderIsOpen] = useState(false);
  const [submitOrderIsOpen, setSubmitOrderIsOpen] = useState(false);
  const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);
  const {
    showModal: cryptoDisclosureModalIsOpen,
    dismissModal: dismissCryptoDisclosureModal,
  } = useDismissableModal('cryptoDisclosure');
  const {
    showModal: stocksDisclosureModalIsOpen,
    dismissModal: dismissStocksDisclosureModal,
  } = useDismissableModal('stocksDisclosure');
  const {
    showModal: smartTradeModalIsOpen,
    dismissModal: dismissSmartTradeModal,
  } = useDismissableModal('smartTrade');

  const {
    data: quoteData,
    isLoading: quoteDataIsLoading,
    isFetching: quoteDataIsFetching,
  } = useGetQuoteDataQuery(
    {
      fromProductCode: fromProductCode?.toUpperCase()!,
      toProductCode: toProductCode?.toUpperCase()!,
      type: type,
    },
    { skip: !fromProductCode || !toProductCode },
  );

  const [initiateQuote, initiateQuoteResult] = useInitiateQuoteMutation();
  const [acceptQuote, acceptQuoteResult] = useAcceptQuoteMutation();

  const quoteResultPrice =
    1 / initiateQuoteResult?.data?.approximateFromToPrice!;

  const lastKnownPrice = 1 / quoteData?.approximateFromProductToToProductRate!;

  const { product: fromProduct } = useProductWithHoldings(fromProductCode!);
  const { product: toProduct } = useProductWithHoldings(toProductCode!);
  let title: string = localizedContent.title(toProduct?.code, toProduct?.name);
  const [selectionCurrencyIsOpen, setSelectionCurrencyIsOpen] =
    useState(!fromProductCode);

  useEffect(() => {
    setSelectedPercentage(null);
  }, [type, toProductCode]);

  useEffect(() => {
    if (fromProductCode && toProductCode) {
      fromAssetFieldRef.current?.focus();
    }
  }, [fromProductCode, toProductCode]);

  useEffect(() => {
    if (acceptQuoteResult.isSuccess) {
      // TRACK BUY NOW EXECUTED
      trackEvent(EVENTS.TRADE_COMPLETE, {
        [tradeEventFields.amountFrom]: acceptQuoteResult?.data?.fromAmount,
        [tradeEventFields.fromCurrencyCode]: fromProductCode,
        [tradeEventFields.toAmountCrypto]: acceptQuoteResult?.data?.toAmount,
        [tradeEventFields.toCurrencyCode]: toProductCode,
        [tradeEventFields.tradeFees]:
          acceptQuoteResult?.data?.baseFeeInFromProduct,
        [tradeEventFields.feeCurrency]: fromProductCode,
        [tradeEventFields.tradeType]: TRANSACTION_TYPE.INVEST,
      });
    }
  }, [acceptQuoteResult]);

  const fromProductAvailableAmount = fromProduct?.availableAmount ?? 0;

  const tradeData = calculateTradeData(
    acceptQuoteResult.isSuccess,
    type,
    fromProductCode,
    toProductCode ?? null,
    initiateQuoteResult?.data,
    quoteData,
    fromAmount,
    fromProduct?.availableAmount,
  );

  const fromInfoText = getFromInfoText(
    tradeData.exceedsBalanceError,
    tradeData.balanceBelowMinimumError,
    tradeData.belowMinimumError,
    tradeData.aboveMaximumError,
    tradeData.minimumFromValue!,
    tradeData.maximumFromValue!,
  );

  async function submit() {
    var res = await initiateQuote({
      fromProductCode: fromProductCode!,
      toProductCode: toProductCode!,
      amount: fromAmount!,
      type: type,
    });

    if (!res.error) {
      setReviewOrderIsOpen(true);
    }
  }

  function handleInfoIconPressed(infoType: InfoType) {
    setInfoType(infoType);
    setInfoModalIsOpen(true);
  }

  async function executeTrade() {
    await acceptQuote({
      orderId: initiateQuoteResult.data?.orderId!,
      type: type,
    });
  }

  function resetTradeExecution() {
    initiateQuoteResult.reset();
    acceptQuoteResult.reset();
  }

  function handlePercentageButtonPressed(percentage: number) {
    if (!fromProductAvailableAmount) return;

    var amount = (fromProductAvailableAmount * percentage) / 100;
    var precision = getAssetPrecision(fromProductCode);
    // Truncate: rounding up here makes the 100% button produce an amount
    // larger than the balance, which then fails its own validation.
    var roundedAmount = truncateToDecimals(amount, precision);

    setSelectedPercentage(percentage);
    setFromAmount(roundedAmount);
  }

  const fromAssetField = (
    <AssetField
      ref={fromAssetFieldRef}
      className="w-full"
      assetIconComponent={CurrencyIcon}
      assetIconProps={{
        code: fromProductCode!,
        size: 25,
        style: { marginRight: 5 },
      }}
      assetCode={fromProductCode?.toUpperCase() ?? undefined}
      assetPlaceholder={localizedContent.select}
      amountReadonly={!fromProductCode || quoteDataIsLoading}
      amount={fromAmount ?? null}
      onValueChanged={amount => {
        setFromAmount(amount);
        setSelectedPercentage(null);
      }}
      hasError={
        (tradeData.fromHasError || tradeData.aboveMaximumError) &&
        !acceptQuoteResult.isLoading
      }
      showPlusMinusButtons={false}
      title={localizedContent.using}
      onPress={() => {
        setSelectingSide('from');
        setSelectionCurrencyIsOpen(true);
      }}
      infoText={fromInfoText}
      onInfoTextPressed={() => handlePercentageButtonPressed(100)}
    />
  );

  const toAssetField = (
    <AssetField
      className="w-full"
      assetCode={toProductCode?.toUpperCase() ?? undefined}
      assetIconComponent={CurrencyIcon}
      assetIconProps={{
        code: toProductCode!,
        size: 25,
        style: { marginRight: 5 },
      }}
      assetPlaceholder={localizedContent.select}
      amountReadonly={true}
      amount={tradeData.toAmount ?? null}
      onValueChanged={() => {}}
      title={localizedContent.buy}
      onPress={() => {
        setSelectingSide('to');
        setSelectionCurrencyIsOpen(true);
      }}
    />
  );

  const percentageButtons = (
    <View className="flex-row gap-2 w-full mt-3">
      {percentageButtonValues.map(e => (
        <RadioButton
          key={e}
          className="flex-1"
          disabled={fromProductCode == null || toProductCode == null}
          selected={e == selectedPercentage}
          title={e + '%'}
          size="large"
          onPress={() => handlePercentageButtonPressed(e)}
        />
      ))}
    </View>
  );

  function getFromInfoText(
    exceedsBalanceError: boolean,
    balanceBelowMinimumError: boolean,
    belowMinimumError: boolean,
    aboveMaximumError: boolean,
    minimumFromValue: number,
    maximumFromValue: number,
  ): string {
    if (
      !fromProductCode ||
      !toProductCode ||
      acceptQuoteResult.isSuccess ||
      quoteDataIsFetching
    )
      return '';

    const formattedBalance = formatCurrency(
      fromProduct?.availableAmount ?? null,
      fromProductCode ?? '',
    );

    if (exceedsBalanceError) {
      return localizedContent.exceedsBalanceOf(formattedBalance);
    }

    const formattedMinimum = formatCurrency(
      minimumFromValue ?? null,
      fromProductCode ?? '',
    );

    const formattedMaximum = formatCurrency(
      maximumFromValue ?? null,
      fromProductCode ?? '',
    );

    if (balanceBelowMinimumError) {
      return localizedContent.youNeedAtLeast(formattedMinimum);
    }

    if (belowMinimumError && minimumFromValue !== null) {
      return localizedContent.enterAMinimumOf(formattedMinimum);
    }

    if (aboveMaximumError && maximumFromValue !== null) {
      return localizedContent.enterAMaximumOf(formattedMaximum);
    }

    return `${formattedBalance} ${localizedContent.available}`;
  }
  const showDisclosureModal =
    toProduct?.type === ActiveProductDtoTypeEnum.Stock
      ? stocksDisclosureModalIsOpen
      : cryptoDisclosureModalIsOpen;

  return (
    <>
      <div className="w-full items-center">
        <div className="flex flex-col w-full items-center flex-1">
          <div className="flex flex-col items-center mb-10">
            <Heading level={4} className="mb-3 mt-5 text-center">
              {title}
            </Heading>
            {tradeData.bothAssetsSelected && quoteData && (
              <InfoText
                title={` ${localizedContent.lastPrice}: `}
                text={formatAssetAmount(
                  lastKnownPrice,
                  type === 'buy' ? fromProductCode! : toProductCode!,
                  { showSpacer: true },
                )}
                hasError={!!acceptQuoteResult.error}
              />
            )}
          </div>
          <>
            {toAssetField}
            <div className="mb-8" />
            {fromAssetField}
            {percentageButtons}
          </>
        </div>
        <div className="w-full pb-2 pt-10 items-center flex flex-col">
          {!!initiateQuoteResult.error && (
            <Typography className="my-5 text-center text-error">
              {localizedContent.tradeError}
            </Typography>
          )}
          <Button
            size="large"
            color="primary"
            disabled={tradeData.hasError || quoteDataIsLoading}
            onPress={() => {
              submit();
            }}
            loading={initiateQuoteResult.isLoading}
            text={localizedContent.reviewBuy}
            className="w-full"
          />
        </div>
      </div>
      {toProduct?.type !== ActiveProductDtoTypeEnum.Stock &&
        cryptoDisclosureModalIsOpen && (
          <CryptoDisclosureModal
            isOpen={cryptoDisclosureModalIsOpen}
            onClose={dontShowAgain =>
              dismissCryptoDisclosureModal(dontShowAgain)
            }
          />
        )}
      {toProduct?.type === ActiveProductDtoTypeEnum.Stock &&
        stocksDisclosureModalIsOpen && (
          <StockDisclosureModal
            isOpen={stocksDisclosureModalIsOpen}
            onClose={dontShowAgain =>
              dismissStocksDisclosureModal(dontShowAgain)
            }
          />
        )}
      {smartTradeModalIsOpen && !showDisclosureModal && (
        <SmartTradeModal
          isOpen={smartTradeModalIsOpen}
          onClose={dontShowAgain => dismissSmartTradeModal(dontShowAgain)}
        />
      )}
      {infoModalIsOpen && (
        <InfoModal
          isOpen={infoModalIsOpen}
          onClose={() => setInfoModalIsOpen(false)}
          infoType={infoType}
        />
      )}
      {selectionCurrencyIsOpen &&
        !showDisclosureModal &&
        !smartTradeModalIsOpen && (
          <SelectionCurrencyModal
            isOpen={selectionCurrencyIsOpen}
            onClose={() => setSelectionCurrencyIsOpen(false)}
            fromProductCode={fromProductCode}
            toProductCode={toProductCode ?? null}
            onFromProductCodeSelected={newFromProduct => {
              if (newFromProduct != fromProductCode) {
                setFromProductCode(newFromProduct);
                setFromAmount(null);
                setSelectedPercentage(null);
              }
            }}
            onToProductCodeSelected={newToAsset => {
              if (newToAsset != toProductCode) {
                router.navigate({
                  to: '/trade/buy-now',
                  search: { productCode: newToAsset },
                });
              }
            }}
            side={selectingSide}
            onSideChanged={side => setSelectingSide(side)}
            type={type}
          />
        )}
      {reviewOrderIsOpen && (
        <ReviewOrderModal
          onInfoIconPressed={handleInfoIconPressed}
          isOpen={reviewOrderIsOpen}
          onClose={() => {
            setReviewOrderIsOpen(false);
          }}
          onSubmit={() => {
            setReviewOrderIsOpen(false);
            setSubmitOrderIsOpen(true);
            executeTrade();
          }}
          type={type}
          fromProductCode={fromProductCode!}
          toProductCode={toProductCode!}
          fromAmount={fromAmount!}
          orderId={initiateQuoteResult.data?.orderId!}
          toAmount={initiateQuoteResult.data?.approximateToAmount!}
          fee={initiateQuoteResult.data?.approximateFinalFeeInFromProduct!}
          price={quoteResultPrice}
          rewardTierName={initiateQuoteResult?.data?.rewardTierName!}
          rewardTierCode={initiateQuoteResult?.data?.rewardTierCode!}
          expiresAt={initiateQuoteResult.data?.expiresAt!}
          currentTime={initiateQuoteResult.data?.currentTime!}
        />
      )}
      {submitOrderIsOpen && (
        <SubmitOrderModal
          type={type}
          tradeComplete={acceptQuoteResult.isSuccess}
          tradeError={acceptQuoteResult.isError}
          fromAmount={fromAmount!}
          toAmount={
            acceptQuoteResult?.data?.toAmount ??
            initiateQuoteResult.data?.approximateToAmount!
          }
          price={quoteResultPrice}
          toProductCode={toProductCode!}
          fromProductCode={fromProductCode!}
          onClose={() => {
            setSubmitOrderIsOpen(false);
            resetTradeExecution();
          }}
          retryTrade={executeTrade}
        />
      )}
    </>
  );
}

export default BuyNowScreen;
